import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { Security, CheckCircle, PhoneAndroid, Email } from '@mui/icons-material';
import mfaApiService from 'api/MfaApiService';
import MfaSetup from '../MfaSetup/MfaSetup';
import MfaMethodSelector from '../MfaMethodSelector/MfaMethodSelector';
import MfaEmailEnable from '../MfaEmailEnable/MfaEmailEnable';
import { useSelector } from 'store/hooks';
import './MfaSettings.scss';

type MfaDialogView = 'none' | 'method_selector' | 'totp_setup' | 'email_enable';

const MFA_METHOD_LABELS: Record<string, { label: string; description: string; icon: React.ReactNode }> = {
  SOFTWARE_TOKEN_MFA: {
    label: 'Authenticator App (TOTP)',
    description:
      'Your account is protected with an Authenticator App (TOTP). You need your authenticator app to log in.',
    icon: <PhoneAndroid fontSize="small" />,
  },
  EMAIL_OTP: {
    label: 'Email OTP',
    description:
      'Your account is protected with Email OTP. A one-time code is sent to your email each time you log in.',
    icon: <Email fontSize="small" />,
  },
};

const MfaSettings: React.FC = () => {
  const appState = useSelector((state) => state.app);
  const userIsAdmin = appState.roles?.includes('admin');
  const userEmail = appState.user?.mail || '';

  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaPreference, setMfaPreference] = useState<'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP' | null>(null);
  const [mfaSetupCompleted, setMfaSetupCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogView, setDialogView] = useState<MfaDialogView>('none');
  const [showDisableDialog, setShowDisableDialog] = useState(false);

  useEffect(() => {
    loadMfaStatus();
  }, []);

  const loadMfaStatus = async () => {
    try {
      setLoading(true);
      const status = await mfaApiService.getMfaStatus();
      setMfaEnabled(status.mfaEnabled);
      setMfaPreference(status.mfaPreference);
      setMfaSetupCompleted(status.mfaSetupCompleted);

      // If MFA is enabled by admin but not set up, automatically show method selector
      if (status.mfaEnabled && !status.mfaSetupCompleted) {
        setDialogView('method_selector');
      }
    } catch (err: any) {
      setError('Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableMfa = () => {
    setDialogView('method_selector');
  };

  const handleMethodSelectTotp = () => {
    setDialogView('totp_setup');
  };

  const handleMethodSelectEmail = () => {
    setDialogView('email_enable');
  };

  const handleCloseDialog = () => {
    // Only allow close if MFA is not required but incomplete
    if (!mfaEnabled || mfaSetupCompleted) {
      setDialogView('none');
    }
  };

  const handleSetupSuccess = () => {
    setDialogView('none');
    setMfaEnabled(true);
    setMfaSetupCompleted(true);
    setMfaPreference('SOFTWARE_TOKEN_MFA');
    setSuccess('MFA has been successfully enabled! Redirecting...');

    sessionStorage.setItem('mfa_check_result', 'passed');
    sessionStorage.setItem('mfa_check_done', 'true');

    setTimeout(() => {
      const customerSlug = appState.customer?.slug;
      if (customerSlug) {
        window.location.href = `/${customerSlug}/dashboard`;
      } else {
        window.location.href = '/';
      }
    }, 1500);
  };

  const handleEmailEnableSuccess = () => {
    setDialogView('none');
    setMfaEnabled(true);
    setMfaSetupCompleted(true);
    setMfaPreference('EMAIL_OTP');
    setSuccess('Email OTP MFA has been successfully enabled! Redirecting...');

    sessionStorage.setItem('mfa_check_result', 'passed');
    sessionStorage.setItem('mfa_check_done', 'true');

    setTimeout(() => {
      const customerSlug = appState.customer?.slug;
      if (customerSlug) {
        window.location.href = `/${customerSlug}/dashboard`;
      } else {
        window.location.href = '/';
      }
    }, 1500);
  };

  const handleDisableMfa = async () => {
    try {
      if (!userIsAdmin) {
        setError('Only an administrator can disable MFA.');
        return;
      }
      setActionLoading(true);
      setError(null);
      await mfaApiService.disableMfa();
      setMfaEnabled(false);
      setMfaSetupCompleted(false);
      setMfaPreference(null);
      setSuccess('MFA has been disabled');
      setShowDisableDialog(false);

      sessionStorage.removeItem('mfa_check_done');
      sessionStorage.removeItem('mfa_check_result');
      sessionStorage.setItem('mfa_check_result', 'passed');

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disable MFA');
    } finally {
      setActionLoading(false);
    }
  };

  const renderMfaActionButton = () => {
    if (!mfaEnabled) {
      return (
        <Button variant="contained" color="primary" onClick={handleEnableMfa} startIcon={<Security />}>
          Enable MFA
        </Button>
      );
    }

    if (userIsAdmin) {
      return (
        <Button variant="outlined" color="error" onClick={() => setShowDisableDialog(true)}>
          Disable MFA
        </Button>
      );
    }

    return null;
  };

  const renderMfaMethodBadge = () => {
    if (!mfaEnabled || !mfaPreference) return null;
    const info = MFA_METHOD_LABELS[mfaPreference];
    if (!info) return null;
    return (
      <Chip
        icon={info.icon as React.ReactElement}
        label={info.label}
        color="primary"
        size="small"
        variant="outlined"
        sx={{ mt: 0.5 }}
      />
    );
  };

  const renderStatusDescription = () => {
    if (!mfaEnabled || !mfaPreference) return null;
    const info = MFA_METHOD_LABELS[mfaPreference];
    return (
      <Typography variant="body2" color="textSecondary">
        {info?.description}
      </Typography>
    );
  };

  if (loading) {
    return (
      <Box className="mfa-settings-loading">
        <CircularProgress />
      </Box>
    );
  }

  const isDialogOpen = dialogView !== 'none';
  const mfaRequiredNotCompleted = mfaEnabled && !mfaSetupCompleted;

  return (
    <Box className="mfa-settings-container">
      <Paper elevation={2} className="mfa-settings-paper">
        <Box className="mfa-settings-header">
          <Security className="mfa-icon" />
          <Typography variant="h6">Two-Factor Authentication (MFA)</Typography>
        </Box>

        {error && (
          <Alert severity="error" className="mfa-alert" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" className="mfa-alert" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box className="mfa-settings-content">
          <Box className="mfa-status-section">
            <Box className="mfa-status-info">
              <Typography variant="body1" className="mfa-status-title">
                Status:
              </Typography>
              {mfaEnabled ? (
                <Chip icon={<CheckCircle />} label="Enabled" color="success" size="small" />
              ) : (
                <Chip label="Disabled" color="default" size="small" />
              )}
            </Box>

            {renderMfaMethodBadge()}
            {renderStatusDescription()}
          </Box>

          <Box className="mfa-description">
            <Typography variant="body2">
              Two-factor authentication adds an extra layer of security to your account. When enabled, you will need to
              provide a second factor — either from your authenticator app or via email — in addition to your password
              when logging in.
            </Typography>
          </Box>

          <Box className="mfa-actions">{renderMfaActionButton()}</Box>

          {mfaEnabled && !userIsAdmin && (
            <Alert severity="info" className="mfa-recovery-info">
              <Typography variant="body2">
                <strong>MFA is enabled.</strong> Only an administrator can disable MFA for your account.
              </Typography>
            </Alert>
          )}

          {mfaEnabled && (
            <Alert severity="info" className="mfa-recovery-info">
              <Typography variant="body2">
                <strong>Important:</strong> If you lose access to your second factor, contact your administrator to
                disable MFA for your account.
              </Typography>
            </Alert>
          )}
        </Box>
      </Paper>

      {/* MFA Setup / Method Selector Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={mfaRequiredNotCompleted}
      >
        <DialogContent>
          {mfaRequiredNotCompleted && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>MFA Setup Required:</strong> Your administrator has enabled multi-factor authentication for your
                account. You must complete the setup to continue using the application.
              </Typography>
            </Alert>
          )}

          {dialogView === 'method_selector' && (
            <MfaMethodSelector
              userEmail={userEmail}
              onSelectTotp={handleMethodSelectTotp}
              onSelectEmail={handleMethodSelectEmail}
              onCancel={mfaRequiredNotCompleted ? undefined : handleCloseDialog}
            />
          )}

          {dialogView === 'totp_setup' && (
            <MfaSetup
              onSuccess={handleSetupSuccess}
              onCancel={mfaRequiredNotCompleted ? undefined : () => setDialogView('method_selector')}
            />
          )}

          {dialogView === 'email_enable' && (
            <MfaEmailEnable
              userEmail={userEmail}
              onSuccess={handleEmailEnableSuccess}
              onCancel={mfaRequiredNotCompleted ? undefined : () => setDialogView('method_selector')}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Disable Confirmation Dialog */}
      {userIsAdmin && (
        <Dialog open={showDisableDialog} onClose={() => setShowDisableDialog(false)}>
          <DialogTitle>Disable Two-Factor Authentication?</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to disable MFA? This will make your account less secure.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDisableDialog(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleDisableMfa} color="error" variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={24} /> : 'Disable MFA'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default MfaSettings;
